import React from 'react';
import { ActionButton } from 'src/tef/components/buttons/ActionButton';
import { useIcon } from 'src/tef/components/hooks/useIcon';
import { useTEF } from 'src/tef/components/providers/TEFProvider';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import type { ComponentSize, ForwardedHTMLProps } from 'src/tef/components/utils/types';
import type { IconProp } from 'src/tef/components/utils/types';

type EmptyStateVariant = 'default' | 'card' | 'inline' | 'minimal';

interface EmptyStateAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'ghost';
  loading?: boolean;
}

interface EmptyStateDisplayProps extends ForwardedHTMLProps {
  icon?: IconProp;
  title: string;
  description?: string;
  action?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  variant?: EmptyStateVariant;
  size?: ComponentSize;
  loading?: boolean;
  className?: string;
}

const sizeConfig: Record<
  ComponentSize,
  {
    container: string;
    icon: string;
    title: string;
    description: string;
    iconWrapper: string;
  }
> = {
  xs: { container: 'py-4 px-2', icon: 'w-6 h-6', iconWrapper: 'w-10 h-10', title: 'text-sm', description: 'text-xs' },
  sm: { container: 'py-6 px-3', icon: 'w-8 h-8', iconWrapper: 'w-12 h-12', title: 'text-base', description: 'text-sm' },
  md: {
    container: 'py-10 px-4',
    icon: 'w-10 h-10',
    iconWrapper: 'w-16 h-16',
    title: 'text-lg',
    description: 'text-base',
  },
  lg: {
    container: 'py-14 px-6',
    icon: 'w-12 h-12',
    iconWrapper: 'w-20 h-20',
    title: 'text-xl',
    description: 'text-lg',
  },
  xl: {
    container: 'py-20 px-8',
    icon: 'w-16 h-16',
    iconWrapper: 'w-24 h-24',
    title: 'text-2xl',
    description: 'text-xl',
  },
};

export const EmptyStateDisplay = React.forwardRef<HTMLDivElement, EmptyStateDisplayProps>(
  (
    {
      icon,
      title,
      description,
      action,
      secondaryAction,
      variant = 'default',
      size = 'md',
      loading = false,
      className,
      ...props
    },
    ref
  ) => {
    const tef = useTEF();
    const forwardedProps = forwardProps(props);
    const iconElement = useIcon(icon);

    const config = sizeConfig[size];

    const containerClasses = cn(
      'text-center',
      config.container,
      variant === 'card' && 'bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700',
      variant === 'inline' && 'py-4 flex items-center gap-4 text-left',
      variant === 'minimal' && 'py-4',
      loading && 'animate-pulse',
      className
    );

    const isInline = variant === 'inline';

    return (
      <section ref={ref} className={containerClasses} role="status" aria-busy={loading} {...forwardedProps} {...props}>
        {icon && (
          <span
            className={cn(
              'flex items-center justify-center',
              config.iconWrapper,
              isInline ? '' : 'mx-auto mb-4',
              'rounded-lg',
              'bg-slate-100 dark:bg-slate-700',
              tef.colors.muted
            )}
            aria-hidden="true"
          >
            <span className={cn(config.icon, 'flex items-center justify-center [&>svg]:w-full [&>svg]:h-full')}>
              {iconElement}
            </span>
          </span>
        )}

        <span className={isInline ? 'flex-1' : ''}>
          <span className={cn('block', isInline ? '' : 'mb-2', config.title, 'font-semibold', tef.colors.foreground)}>
            {title}
          </span>

          {description && (
            <span
              className={cn(
                'block',
                isInline ? '' : action || secondaryAction ? 'mb-6' : '',
                config.description,
                tef.colors.foregroundTertiary,
                'max-w-md',
                !isInline && 'mx-auto'
              )}
            >
              {description}
            </span>
          )}
        </span>

        {(action || secondaryAction) && !isInline && (
          <span className="flex items-center justify-center gap-3 flex-wrap">
            {action && (
              <ActionButton
                variant={{ style: action.variant || 'primary', size }}
                onClick={action.onClick}
                loading={action.loading}
                disabled={loading}
              >
                {action.label}
              </ActionButton>
            )}
            {secondaryAction && (
              <ActionButton
                variant={{ style: secondaryAction.variant || 'ghost', size }}
                onClick={secondaryAction.onClick}
                loading={secondaryAction.loading}
                disabled={loading}
              >
                {secondaryAction.label}
              </ActionButton>
            )}
          </span>
        )}
      </section>
    );
  }
);

EmptyStateDisplay.displayName = 'EmptyStateDisplay';

export default EmptyStateDisplay;
